import { useState, useEffect, useCallback, useRef } from 'react';
import { ChatBase, ChatState, ChatHistoryCache, CreateChatPayload, ChatHistoryMessage } from '../types/chatBase';
import { ChatBaseService, PlaygroundMultiChatService } from '../services/chatBaseService';
import { useWorkspace } from '@/shared/hooks/api/useWorkspace';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/shared/components/ui/use-toast';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';

/**
 * Hook para gerenciar múltiplos chats no Playground
 * Fornece estado global, cache e operações CRUD para chats
 */
/**
 * Chave de deduplicação por conteúdo — só para mensagens com texto. A linha `thinking`
 * (o Pensamento do agente, AB#87296) tem conteúdo vazio: comparar por conteúdo faria toda
 * linha de Pensamento depois da primeira parecer repetida e sumir da conversa.
 */
function contentKey(msg: ChatHistoryMessage): string | null {
  return msg.content ? `${msg.content}_${msg.message_type}` : null;
}

export function useChatBase() {
  const { t } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const { toast: shadcnToast } = useToast();
  const [state, setState] = useState<ChatState>({
    chats: [],
    selectedChatId: null,
    isLoadingChats: false,
    isCreatingChat: false,
    isSidebarOpen: false,
    error: null,
  });

  // Cache de histórico por chatId
  const historyCache = useRef<ChatHistoryCache>({});

  // Cache local storage key
  const storageKey = currentWorkspace?.id ? `playground-chats-${currentWorkspace.id}` : null;
  const selectedChatKey = currentWorkspace?.id ? `playground-selected-chat-${currentWorkspace.id}` : null;

  /**
   * Carregar chats do localStorage se disponível
   */
  const loadChatsFromStorage = useCallback(() => {
    if (!storageKey) return [];

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      console.warn('[useChatBase] Failed to load chats from localStorage:', error);
    }

    return [];
  }, [storageKey]);

  /**
   * Salvar chats no localStorage
   */
  const saveChatsToStorage = useCallback((chats: ChatBase[]) => {
    if (!storageKey) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(chats));
    } catch (error) {
      console.warn('[useChatBase] Failed to save chats to localStorage:', error);
    }
  }, [storageKey]);

  /**
   * Carregar lista de chats do produto
   */
  const loadChats = useCallback(async () => {
    if (!currentWorkspace?.id) return;

    setState(prev => ({ ...prev, isLoadingChats: true, error: null }));

    try {
      const { data: chats, error } = await ChatBaseService.getChats(currentWorkspace.id);

      if (error) {
        setState(prev => ({ ...prev, error, isLoadingChats: false }));
        return;
      }

      // Ordenar por última atividade (mais recentes primeiro)
      const sortedChats = chats.sort((a, b) => {
        const aTime = new Date(a.last_message_at || a.updated_at).getTime();
        const bTime = new Date(b.last_message_at || b.updated_at).getTime();
        return bTime - aTime;
      });

      setState(prev => ({
        ...prev,
        chats: sortedChats,
        isLoadingChats: false,
        error: null
      }));

      // Salvar no localStorage
      saveChatsToStorage(sortedChats);

    } catch (error: unknown) {
      console.error('[useChatBase] Error loading chats:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : t('playground.errors.loadHistory'),
        isLoadingChats: false
      }));
    }
  }, [currentWorkspace?.id, saveChatsToStorage, t]);

  /**
   * Criar novo chat
   */
  const createChat = useCallback(async (payload: CreateChatPayload = {}) => {
    if (!currentWorkspace?.id) return null;

    setState(prev => ({ ...prev, isCreatingChat: true, error: null }));

    try {
      const { data: newChat, error } = await ChatBaseService.createChat(currentWorkspace.id, payload);

      if (error || !newChat) {
        // Verificar se é erro de subscription
        const isSubscriptionError = error &&
          (error.includes('Subscription') || error.includes('subscription'));

        if (isSubscriptionError) {
          // Exibir toast para erro de subscription (NÃO seta error no estado)
          shadcnToast({
            title: t('knowledge.errors.subscriptionRequired'),
            description: t('knowledge.errors.subscriptionRequiredDescription'),
            variant: "destructive",
          });
          setState(prev => ({ ...prev, isCreatingChat: false, error: null }));
        } else {
          // Exibir erro normal no estado (aparece no chat)
          setState(prev => ({ ...prev, error: error || t('playground.chatList.createError'), isCreatingChat: false }));
        }

        return null;
      }

      // Adicionar à lista e selecionar
      setState(prev => ({
        ...prev,
        chats: [newChat, ...prev.chats],
        selectedChatId: newChat.id,
        isCreatingChat: false,
        error: null
      }));

      // Persistir seleção
      if (selectedChatKey) sessionStorage.setItem(selectedChatKey, newChat.id);

      // Atualizar localStorage
      const updatedChats = [newChat, ...state.chats];
      saveChatsToStorage(updatedChats);

      return newChat;

    } catch (error: unknown) {
      console.error('[useChatBase] Error creating chat:', error);

      const errorMessage = error instanceof Error ? error.message : '';

      // Verificar se é erro de subscription
      const isSubscriptionError = errorMessage &&
        (errorMessage.includes('Subscription') || errorMessage.includes('subscription'));

      if (isSubscriptionError) {
        // Exibir toast para erro de subscription (NÃO seta error no estado)
        shadcnToast({
          title: t('knowledge.errors.subscriptionRequired'),
          description: t('knowledge.errors.subscriptionRequiredDescription'),
          variant: "destructive",
        });
        setState(prev => ({ ...prev, isCreatingChat: false, error: null }));
      } else {
        // Exibir erro normal no estado (aparece no chat)
        setState(prev => ({
          ...prev,
          error: errorMessage || t('playground.chatList.createError'),
          isCreatingChat: false
        }));
      }

      return null;
    }
  }, [currentWorkspace?.id, state.chats, saveChatsToStorage, t, shadcnToast, selectedChatKey]);

  /**
   * Selecionar chat
   */
  const selectChat = useCallback((chatId: string | null) => {
   setState(prev => {
     const newState = { ...prev, selectedChatId: chatId };
     return newState;
    });
    // Persistir seleção na sessionStorage
    if (selectedChatKey) {
      if (chatId) {
        sessionStorage.setItem(selectedChatKey, chatId);
      } else {
        sessionStorage.removeItem(selectedChatKey);
      }
    }
  }, [selectedChatKey]);

  /**
   * Renomear chat
   */
  const renameChat = useCallback(async (chatId: string, newTitle: string) => {
    if (!newTitle.trim()) {
      toast.error(t('playground.chatList.emptyTitleError'));
      return false;
    }

    if (newTitle.length > 255) {
      toast.error(t('playground.chatList.titleTooLongError'));
      return false;
    }

    try {
      const { success, error } = await ChatBaseService.renameChat(chatId, { title: newTitle.trim() });

      if (!success) {
        toast.error(error || t('playground.chatList.renameError'));
        return false;
      }

      // Atualizar na lista local
      setState(prev => ({
        ...prev,
        chats: prev.chats.map(chat =>
          chat.id === chatId
            ? { ...chat, title: newTitle.trim(), updated_at: new Date().toISOString() }
            : chat
        )
      }));

      // Atualizar localStorage
      const updatedChats = state.chats.map(chat =>
        chat.id === chatId
          ? { ...chat, title: newTitle.trim(), updated_at: new Date().toISOString() }
          : chat
      );
      saveChatsToStorage(updatedChats);

      toast.success(t('playground.chatList.chatRenamed'));
      return true;

    } catch (error: unknown) {
      console.error('[useChatBase] Error renaming chat:', error);
      toast.error(resolveApiErrorMessage(error, t));
      return false;
    }
  }, [state.chats, saveChatsToStorage, t]);

  /**
   * Arquivar chat
   */
  const archiveChat = useCallback(async (chatId: string) => {
    try {
      const { success, error } = await ChatBaseService.archiveChat(chatId);

      if (!success) {
        toast.error(error || t('playground.chatList.archiveError'));
        return false;
      }

      // Remover da lista (chats arquivados não aparecem na lista ativa)
      setState(prev => ({
        ...prev,
        chats: prev.chats.filter(chat => chat.id !== chatId),
        selectedChatId: prev.selectedChatId === chatId ? null : prev.selectedChatId
      }));

      // Atualizar localStorage
      const updatedChats = state.chats.filter(chat => chat.id !== chatId);
      saveChatsToStorage(updatedChats);

      // Limpar cache do histórico
      if (historyCache.current[chatId]) {
        delete historyCache.current[chatId];
      }

      toast.success(t('playground.chatList.chatArchived'));
      return true;

    } catch (error: unknown) {
      console.error('[useChatBase] Error archiving chat:', error);
      toast.error(resolveApiErrorMessage(error, t));
      return false;
    }
  }, [state.chats, saveChatsToStorage, t]);

  /**
   * Excluir chat
   */
  const deleteChat = useCallback(async (chatId: string) => {
    try {
      const { success, error } = await ChatBaseService.deleteChat(chatId);

      if (!success) {
        toast.error(error || t('playground.chatList.deleteError'));
        return false;
      }

      // Remover da lista
      setState(prev => ({
        ...prev,
        chats: prev.chats.filter(chat => chat.id !== chatId),
        selectedChatId: prev.selectedChatId === chatId ? null : prev.selectedChatId
      }));

      // Atualizar localStorage
      const updatedChats = state.chats.filter(chat => chat.id !== chatId);
      saveChatsToStorage(updatedChats);

      // Limpar cache do histórico
      if (historyCache.current[chatId]) {
        delete historyCache.current[chatId];
      }

      toast.success(t('playground.chatList.chatDeleted'));
      return true;

    } catch (error: unknown) {
      console.error('[useChatBase] Error deleting chat:', error);
      toast.error(resolveApiErrorMessage(error, t));
      return false;
    }
  }, [state.chats, saveChatsToStorage, t]);

  /**
   * Abrir/fechar sidebar
   */
  const toggleSidebar = useCallback(() => {
    setState(prev => ({ ...prev, isSidebarOpen: !prev.isSidebarOpen }));
  }, []);

  /**
   * Obter histórico de um chat específico
   */
  const getChatHistory = useCallback(async (chatId: string) => {

    if (!currentWorkspace?.id) {
   return null;
    }

    // Verificar cache primeiro
    if (historyCache.current[chatId]) {
      const cached = historyCache.current[chatId];
      // Cache válido por 5 minutos
      const isValid = new Date().getTime() - cached.lastFetched.getTime() < 5 * 60 * 1000;
      if (isValid) {
      return cached.messages;
      }
    }

    try {
     const { data, error } = await PlaygroundMultiChatService.getChatHistoryWithChatId({ chatId });


      // Log detalhado das mensagens retornadas
      if (data?.messages) {
       data.messages.forEach((_msg: ChatHistoryMessage, _index: number) => {
      });
      }

      if (error) {
        console.error('[useChatBase] Error fetching chat history:', error);
        return null;
      }

      // Mesclar com mensagens existentes no cache para evitar duplicação
      const existingCache = historyCache.current[chatId]?.messages || [];
      const apiMessages = data.messages || [];

      // Criar mapa de mensagens existentes por ID e conteúdo
      const existingMessagesMap = new Map<string, ChatHistoryMessage>();
      existingCache.forEach((msg: ChatHistoryMessage) => {
        existingMessagesMap.set(msg.id, msg);
        const key = contentKey(msg);
        if (key) existingMessagesMap.set(key, msg);
      });

      // Filtrar mensagens da API que já existem no cache
      const newApiMessages = apiMessages.filter((msg: ChatHistoryMessage) => {
        const key = contentKey(msg);
        return !existingMessagesMap.has(msg.id) && !(key && existingMessagesMap.has(key));
      });

      // Mesclar mensagens existentes com novas da API, ordenadas por data
      const mergedMessages = [...existingCache, ...newApiMessages].sort((a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );


      // Salvar no cache
      historyCache.current[chatId] = {
        messages: mergedMessages,
        lastFetched: new Date()
      };

    return mergedMessages;

    } catch (error: unknown) {
      console.error('[useChatBase] Error fetching chat history:', error);
      return null;
    }
  }, [currentWorkspace?.id]);

  /**
   * Limpar cache de histórico
   */
  const clearHistoryCache = useCallback((chatId?: string) => {
    if (chatId) {
      delete historyCache.current[chatId];
    } else {
      historyCache.current = {};
    }
  }, []);

  /**
   * Atualizar cache de histórico com novas mensagens (substitui todo o array)
   */
  const updateHistoryCache = useCallback((chatId: string, newMessages: ChatHistoryMessage[]) => {

    // Atualizar cache com as mensagens mais recentes
    historyCache.current[chatId] = {
      messages: newMessages,
      lastFetched: new Date()
    };
  }, []);

  /**
   * Adicionar uma única mensagem ORIGINAL ao cache (incrementalmente)
   */
  const addMessageToCache = useCallback((chatId: string, originalMessage: ChatHistoryMessage) => {

    // Buscar histórico atual ou inicializar vazio
    const currentHistory = historyCache.current[chatId]?.messages || [];

    // Verificar se a mensagem já existe (evitar duplicação)
    const originalKey = contentKey(originalMessage);
    const messageExists = currentHistory.some((msg: ChatHistoryMessage) =>
      msg.id === originalMessage.id || (!!originalKey && contentKey(msg) === originalKey)
    );

    if (messageExists) {
    return;
    }

    // Adicionar nova mensagem aos dados originais
    const updatedHistory = [...currentHistory, originalMessage];

    // Atualizar cache com dados originais. `lastFetched` NÃO é renovado: o que entra aqui é
    // a versão local do turno, sem o que só o servidor grava — o Pensamento do agente vira
    // uma linha `thinking` com o rastro (AB#87296). Renovar faria a conversa reaberta nos
    // próximos 5 minutos mostrar a resposta sem o Pensamento; assim, a próxima abertura
    // busca o histórico no servidor e mescla com o que já está aqui.
    historyCache.current[chatId] = {
      messages: updatedHistory,
      lastFetched: new Date(0)
    };

}, []);

  /**
   * Atualizar metadados do chat na lista (contador, última mensagem)
   */
  const updateChatMetadata = useCallback((chatId: string, newMessageCount?: number, lastMessage?: { created_at?: string; [key: string]: unknown }) => {

    setState(prev => ({
      ...prev,
      chats: prev.chats.map(chat => {
        if (chat.id === chatId) {
          const updatedChat = { ...chat };

          // Atualizar contador de mensagens se fornecido
          if (typeof newMessageCount === 'number') {
            updatedChat.message_count = newMessageCount;
          }

          // Atualizar timestamp da última mensagem
          if (lastMessage) {
            updatedChat.last_message_at = lastMessage.created_at || new Date().toISOString();
            updatedChat.updated_at = new Date().toISOString();
          }

          return updatedChat;
        }
        return chat;
      })
    }));

    // Reordenar chats por última atividade
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        chats: [...prev.chats].sort((a, b) => {
          const aTime = new Date(a.last_message_at || a.updated_at).getTime();
          const bTime = new Date(b.last_message_at || b.updated_at).getTime();
          return bTime - aTime;
        })
      }));
    }, 100);

  }, []);

  // Carregar chats quando o produto mudar
  useEffect(() => {
    if (currentWorkspace?.id) {
      // Carregar do localStorage primeiro
      const cachedChats = loadChatsFromStorage();

      // Restaurar chat selecionado da sessionStorage
      const savedSelectedChatId = selectedChatKey ? sessionStorage.getItem(selectedChatKey) : null;

      // O cache de histórico é indexado por chatId e não tem escopo de
      // workspace; ao trocar de workspace ele precisa ser zerado para não
      // servir mensagens do workspace anterior.
      historyCache.current = {};

      // Reescopar o estado para o workspace atual. Tanto `chats` quanto
      // `selectedChatId` são derivados de chaves com escopo de workspace
      // (storageKey/selectedChatKey), então NÃO se pode herdar
      // `prev.selectedChatId` — ele pertence ao workspace anterior e faria o
      // chat antigo continuar aberto após a troca.
      setState(prev => ({
        ...prev,
        chats: cachedChats,
        selectedChatId: savedSelectedChatId,
      }));

      // Depois carregar da API
      loadChats();
    } else {
      setState({
        chats: [],
        selectedChatId: null,
        isLoadingChats: false,
        isCreatingChat: false,
        isSidebarOpen: false,
        error: null,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id, loadChats, loadChatsFromStorage]);

  return {
    // Estado
    ...state,

    // Chat selecionado
    selectedChat: state.chats.find(chat => chat.id === state.selectedChatId) || null,

    // Ações
    loadChats,
    createChat,
    selectChat,
    renameChat,
    archiveChat,
    deleteChat,
    toggleSidebar,
    getChatHistory,
    clearHistoryCache,
    updateHistoryCache,
    updateChatMetadata,
    addMessageToCache,

    // Utilitários
    hasChats: state.chats.length > 0,
    chatsCount: state.chats.length,
  };
}
